/**
 * CustomBenchmarkEditor — inline editor for the user's custom blend.
 * Three percentage rows (MSCI ACWI, Global Agg, Gold), live total badge,
 * Apply enables only at 100%, optional Save-as-default.
 */
import React, { useState, useEffect } from 'react';
import { Check, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COMPONENTS = [
  { id: 'MSCI_ACWI',            label: 'MSCI ACWI',              hint: 'Global equities' },
  { id: 'BLOOMBERG_GLOBAL_AGG', label: 'Bloomberg Global Agg',   hint: 'Global investment-grade bonds' },
  { id: 'GOLD_LBMA',            label: 'Gold (LBMA)',            hint: 'Physical gold' },
];

const authHeaders = (json = false) => {
  const t = localStorage.getItem('auth_token');
  const h = t ? { Authorization: `Bearer ${t}` } : {};
  if (json) h['Content-Type'] = 'application/json';
  return h;
};

export default function CustomBenchmarkEditor({ open, onClose, onSaved, onCancel }) {
  const [weights, setWeights] = useState({ MSCI_ACWI: 60, BLOOMBERG_GLOBAL_AGG: 30, GOLD_LBMA: 10 });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetch(`${API_URL}/api/users/me/custom-benchmark`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => { setWeights(d.weights || weights); setLoaded(true); })
      .catch(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const total = COMPONENTS.reduce((acc, c) => acc + Number(weights[c.id] || 0), 0);
  const totalValid = Math.abs(total - 100) < 0.001;
  const anyPositive = COMPONENTS.some((c) => Number(weights[c.id] || 0) > 0);

  const setWeight = (id, v) => {
    const num = Math.max(0, Math.min(100, Number(v || 0)));
    setWeights((prev) => ({ ...prev, [id]: num }));
  };

  const handleSave = async (applyOnly = false) => {
    if (!totalValid || !anyPositive) return;
    setSaving(true);
    try {
      if (!applyOnly) {
        const r = await fetch(`${API_URL}/api/users/me/custom-benchmark`, {
          method: 'PUT',
          headers: authHeaders(true),
          body: JSON.stringify({ weights }),
        });
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.detail || `Save failed (${r.status})`);
        }
        toast.success('Custom benchmark saved.');
      }
      onSaved?.();
    } catch (e) {
      toast.error(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-4" data-testid="custom-benchmark-editor">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-semibold text-slate-800">Your custom benchmark</div>
          <div className="text-xs text-slate-500">Define your strategic allocation — weights must total exactly 100%.</div>
        </div>
        <div className={`text-sm font-mono font-semibold px-2 py-0.5 rounded ${
          totalValid ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
        }`} data-testid="custom-benchmark-total">
          Total: {total.toFixed(1)}% {totalValid && <Check className="inline w-3.5 h-3.5 ml-0.5" />}
        </div>
      </div>

      <div className="space-y-2.5 mb-4">
        {COMPONENTS.map((c) => (
          <div key={c.id} className="flex items-center gap-3" data-testid={`custom-row-${c.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-800">{c.label}</div>
              <div className="text-xs text-slate-500">{c.hint}</div>
            </div>
            <input
              type="number" min="0" max="100" step="0.1"
              value={weights[c.id] ?? 0}
              onChange={(e) => setWeight(c.id, e.target.value)}
              className="w-20 text-right text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white text-slate-700"
              data-testid={`custom-input-${c.id}`}
              disabled={!loaded}
            />
            <span className="text-sm text-slate-500 w-4">%</span>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900"
          data-testid="custom-benchmark-cancel"
        >Cancel</button>
        <button
          onClick={() => handleSave(true)}
          disabled={!totalValid || !anyPositive || saving}
          className="px-3 py-1.5 text-sm bg-white border border-slate-300 text-slate-700 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="custom-benchmark-apply"
        >Apply only</button>
        <button
          onClick={() => handleSave(false)}
          disabled={!totalValid || !anyPositive || saving}
          className="px-3 py-1.5 text-sm bg-slate-900 text-white rounded-md hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          data-testid="custom-benchmark-save"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save as default
        </button>
      </div>
    </div>
  );
}
